import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Reactivate() {
  const [params] = useSearchParams();
  const [state, setState] = useState("verifying"); // verifying | success | already | error
  const [detail, setDetail] = useState({});
  const userId = params.get("u");
  const token = params.get("t");

  useEffect(() => {
    // noindex — private link
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (!userId || !token) { setState("error"); setDetail({ message: "Link non valido: parametri mancanti." }); return; }
    (async () => {
      try {
        const { data } = await api.post("/owner/reactivate-by-token", { user_id: userId, token });
        if (data.already_active) { setState("already"); setDetail(data); }
        else { setState("success"); setDetail(data); }
      } catch (e) {
        setState("error");
        setDetail({ message: e.response?.data?.detail || "Impossibile verificare il link." });
      }
    })();
  }, [userId, token]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white border border-border rounded-3xl p-10 shadow-xl text-center">
        {state === "verifying" && (
          <>
            <Loader2 className="w-14 h-14 text-[#005C7A] mx-auto animate-spin mb-4" />
            <h1 className="font-display text-2xl font-bold">Verifica in corso…</h1>
            <p className="text-sm text-muted-foreground mt-2">Stiamo riattivando il tuo hotel.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <div className="overline text-green-700 mb-2">RIATTIVATO CON SUCCESSO</div>
            <h1 className="font-display text-3xl font-bold mb-2">Il tuo hotel è tornato online! 🎉</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Abbiamo riattivato <strong>{detail.pools_restored}</strong> {detail.pools_restored === 1 ? "piscina" : "piscine"} per <strong>{detail.hotel_email}</strong>. Sono di nuovo visibili nella ricerca e sulla mappa.
            </p>
            <Link to="/auth?role=owner">
              <Button className="rounded-full bg-[#005C7A] hover:bg-[#004A63] w-full" data-testid="reactivate-login-cta">
                <PlayCircle className="w-4 h-4 mr-2" /> Accedi alla dashboard
              </Button>
            </Link>
          </>
        )}
        {state === "already" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-blue-600" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">Hotel già attivo</h1>
            <p className="text-sm text-muted-foreground mb-6">
              L'account <strong>{detail.hotel_email}</strong> risulta già attivo — non serve fare nulla.
            </p>
            <Link to="/auth?role=owner">
              <Button variant="outline" className="rounded-full w-full">Vai al login</Button>
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-9 h-9 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">Link non valido</h1>
            <p className="text-sm text-muted-foreground mb-6">{detail.message}</p>
            <Link to="/auth?role=owner">
              <Button variant="outline" className="rounded-full w-full">Vai al login</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
